import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { PageHero, SectionLabel } from "@/components/Bits";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COMPANY } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "rounded-none border-line bg-white focus-visible:ring-1 focus-visible:ring-navy focus-visible:border-navy";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    agency_size: "",
    inquiry_type: "general",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Thank you — your inquiry has been received.");
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        agency_size: "",
        inquiry_type: "general",
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact"
        description="Contact Commercial Client Acquisition to discuss building a predictable commercial insurance pipeline for your agency."
        path="/contact"
      />
      <PageHero
        eyebrow="Contact"
        title="Start a conversation with CCA."
        intro="Below please tell us about your agency."
      />

      <section className="bg-paper py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 md:grid-cols-12 md:px-12 lg:px-16">
          <Reveal className="md:col-span-4">
            <SectionLabel num="/">Direct</SectionLabel>
            <div className="mt-8 space-y-8">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-subtext">Email</p>
                <a href={`mailto:${COMPANY.email}`} className="mt-2 block text-lg text-ink hover:text-navy">
                  {COMPANY.email}
                </a>
              </div>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-subtext">Phone</p>
                <p className="mt-2 text-lg text-ink">{COMPANY.phone}</p>
              </div>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-subtext">Category</p>
                <p className="mt-2 text-lg text-ink">{COMPANY.category}</p>
              </div>
            </div>
          </Reveal>

          <Reveal className="md:col-span-7 md:col-start-6" delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="border border-line bg-white p-8 md:p-10">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="c-name">Full name *</Label>
                  <Input id="c-name" data-testid="contact-name" className={field} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-email">Email *</Label>
                  <Input id="c-email" type="email" data-testid="contact-email" className={field} value={form.email} onChange={set("email")} placeholder="jane@agency.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-company">Agency / Company</Label>
                  <Input id="c-company" data-testid="contact-company" className={field} value={form.company} onChange={set("company")} placeholder="Doe Commercial Insurance" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-phone">Phone</Label>
                  <Input id="c-phone" data-testid="contact-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
                </div>
                <div className="space-y-2">
                  <Label>Agency size</Label>
                  <Select value={form.agency_size} onValueChange={(v) => setForm((f) => ({ ...f, agency_size: v }))}>
                    <SelectTrigger data-testid="contact-agency-size" className={field}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-3 producers">1–3 producers</SelectItem>
                      <SelectItem value="4-10 producers">4–10 producers</SelectItem>
                      <SelectItem value="11-25 producers">11–25 producers</SelectItem>
                      <SelectItem value="25+ producers">25+ producers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Inquiry type</Label>
                  <Select value={form.inquiry_type} onValueChange={(v) => setForm((f) => ({ ...f, inquiry_type: v }))}>
                    <SelectTrigger data-testid="contact-inquiry-type" className={field}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General inquiry</SelectItem>
                      <SelectItem value="services">Pipeline services</SelectItem>
                      <SelectItem value="partnership">Strategic partnership</SelectItem>
                      <SelectItem value="careers">Careers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-6 space-y-2">
                <Label htmlFor="c-message">Message *</Label>
                <Textarea id="c-message" data-testid="contact-message" rows={5} className={field} value={form.message} onChange={set("message")} placeholder="Tell us a little about your ideal prospect." />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="group mt-8 inline-flex items-center gap-2 border border-ink bg-ink px-7 py-3.5 font-mono text-[12px] uppercase tracking-[0.12em] text-paper transition-colors hover:bg-transparent hover:text-ink disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {loading ? "Sending" : "Send Inquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

    </div>
  );
}
